import { c as _c } from "react/compiler-runtime";
import { getFormattedCurrency } from '@svs/nova-poolbetting/core/lib/client';
import gsap from 'gsap';
import { type ReactNode, useEffect, useRef } from 'react';
import { gsapCounterEffect } from './GsapCustomEffects/gsapCounterEffect';
if (!gsap.effects.customCounter) {
  gsap.registerEffect({
    name: 'customCounter',
    extendTimeline: true,
    effect: gsapCounterEffect
  });
}
type CurrencyCounterProps = {
  amount: number;
  className?: string;
  duration?: number;
  uniqueId: string;
};

/**
 * CurrencyCounter component displays a counter for currency values.
 *
 * @param props - The component props.
 * @param props.amount - The amount of currency to display.
 * @param props.className - Additional CSS class name(s) for the component.
 * @param props.duration - The duration of the counter animation in seconds.
 * @param props.uniqueId - A unique identifier for the component for forcing a remount behavior. Used to prevent animation when toggling between values.
 * @returns The rendered CurrencyCounter component.
 */
export default function CurrencyCounter(t0) {
  const $ = _c(14);
  const {
    amount,
    className,
    duration,
    uniqueId
  } = t0;
  const currencyDuration = duration ?? 2;
  const previousAmount = useRef(amount);
  const prevUniqueId = useRef(uniqueId);
  const isInitialRender = useRef(true);
  const counterRef = useRef(null);
  let classNames;
  if ($[0] !== className) {
    classNames = ["currency-counter"];
    if (className) {
      classNames.push(className);
    }
    $[0] = className;
    $[1] = classNames;
  } else {
    classNames = $[1];
  }
  let t1;
  let t2;
  if ($[2] !== amount || $[3] !== currencyDuration || $[4] !== uniqueId) {
    t1 = () => {
      let timeline;
      if (uniqueId !== prevUniqueId.current) {
        prevUniqueId.current = uniqueId;
        previousAmount.current = amount;
        isInitialRender.current = true;
      }
      if (!isInitialRender.current) {
        if (!Number.isFinite(amount)) {
          previousAmount.current = 0;
        } else {
          if (counterRef.current && previousAmount.current !== amount) {
            const hasPrefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
            timeline = gsap.timeline();
            timeline.customCounter(counterRef.current, {
              start: previousAmount.current,
              end: amount,
              duration: hasPrefersReducedMotion ? 0 : currencyDuration
            });
            previousAmount.current = amount;
          }
        }
      }
      isInitialRender.current = false;
      return () => {
        timeline?.kill();
      };
    };
    t2 = [amount, currencyDuration, uniqueId];
    $[2] = amount;
    $[3] = currencyDuration;
    $[4] = uniqueId;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  if (!Number.isFinite(amount)) {
    const t3 = classNames.join(" ");
    let t4;
    if ($[7] !== t3) {
      t4 = <span className={t3}>-</span>;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  const t3 = classNames.join(" ");
  let t4;
  if ($[9] !== amount) {
    t4 = getFormattedCurrency(amount);
    $[9] = amount;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <span className={t3} ref={counterRef}>{t4}</span>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}